import { z } from 'zod';

import { weekdaySchema } from './check-in.js';
import { dateOnlySchema, isoDateTimeSchema, uuidSchema } from './common.js';

export const bookingSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase kebab-case');

export const bookingDurationSchema = z.union([z.literal(30), z.literal(60)]);
export type BookingDuration = z.infer<typeof bookingDurationSchema>;

export const availabilityOverrideKindSchema = z.enum(['open', 'blocked']);

export const bookingPageSchema = z.object({
  id: uuidSchema,
  hostUserId: uuidSchema,
  slug: bookingSlugSchema,
  title: z.string().min(1).max(200),
  description: z.string().max(5000).nullable(),
  allowDuration30: z.boolean(),
  allowDuration60: z.boolean(),
  bufferMinutes: z.number().int().min(0).max(120),
  minNoticeHours: z.number().int().min(0).max(168),
  maxDaysAhead: z.number().int().min(1).max(90),
  active: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type BookingPageDto = z.infer<typeof bookingPageSchema>;

export const publicBookingPageSchema = z.object({
  slug: bookingSlugSchema,
  title: z.string().min(1).max(200),
  description: z.string().max(5000).nullable(),
  active: z.boolean(),
  allowDuration30: z.boolean(),
  allowDuration60: z.boolean(),
  hostDisplayName: z.string().min(1).max(240),
  hostTimeZone: z.literal('Europe/Moscow'),
});
export type PublicBookingPageDto = z.infer<typeof publicBookingPageSchema>;

export const availabilityRuleSchema = z.object({
  id: uuidSchema,
  bookingPageId: uuidSchema,
  weekday: weekdaySchema,
  startMinute: z.number().int().min(0).max(1440),
  endMinute: z.number().int().min(0).max(1440),
});
export type AvailabilityRuleDto = z.infer<typeof availabilityRuleSchema>;

export const availabilityOverrideSchema = z.object({
  id: uuidSchema,
  bookingPageId: uuidSchema,
  date: dateOnlySchema,
  startMinute: z.number().int().min(0).max(1440),
  endMinute: z.number().int().min(0).max(1440),
  kind: availabilityOverrideKindSchema,
});
export type AvailabilityOverrideDto = z.infer<typeof availabilityOverrideSchema>;

export const bookingSlotSchema = z.object({
  startAt: isoDateTimeSchema,
  endAt: isoDateTimeSchema,
});
export type BookingSlotDto = z.infer<typeof bookingSlotSchema>;

export const publicBookingSlotsResponseSchema = z.object({
  slots: z.array(bookingSlotSchema),
  hostTimeZone: z.literal('Europe/Moscow'),
});
export type PublicBookingSlotsResponseDto = z.infer<typeof publicBookingSlotsResponseSchema>;

export const bookingSchema = z.object({
  id: uuidSchema,
  bookingPageId: uuidSchema,
  hostUserId: uuidSchema,
  startAt: isoDateTimeSchema,
  endAt: isoDateTimeSchema,
  inviteeName: z.string().min(1).max(200),
  inviteeEmail: z.string().email().max(254),
  inviteeNote: z.string().max(5000).nullable(),
  inviteeTimezone: z.string().min(1).max(64),
  status: z.enum(['confirmed', 'cancelled']),
  hostConfirmedAt: isoDateTimeSchema.nullable(),
  videoMeetingUrl: z.string().max(2048).nullable(),
  videoLinkSentAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type BookingDto = z.infer<typeof bookingSchema>;

export const bookingConfirmationSchema = z.object({
  booking: bookingSchema,
  cancelToken: z.string().min(1).max(64),
  cancelUrl: z.string().min(1),
  hostTimeZone: z.literal('Europe/Moscow'),
});
export type BookingConfirmationDto = z.infer<typeof bookingConfirmationSchema>;

export const bookingPageBundleSchema = z.object({
  page: bookingPageSchema,
  rules: z.array(availabilityRuleSchema),
  overrides: z.array(availabilityOverrideSchema),
});
export type BookingPageBundleDto = z.infer<typeof bookingPageBundleSchema>;

export const updateBookingPageSchema = z
  .object({
    slug: bookingSlugSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(5000).nullable(),
    allowDuration30: z.boolean(),
    allowDuration60: z.boolean(),
    bufferMinutes: z.number().int().min(0).max(120),
    minNoticeHours: z.number().int().min(0).max(168),
    maxDaysAhead: z.number().int().min(1).max(90),
    active: z.boolean(),
  })
  .partial();
export type UpdateBookingPageDto = z.infer<typeof updateBookingPageSchema>;

export const availabilityRuleInputSchema = z
  .object({
    weekday: weekdaySchema,
    startMinute: z.number().int().min(0).max(1440),
    endMinute: z.number().int().min(0).max(1440),
  })
  .refine((r) => r.startMinute < r.endMinute, {
    message: 'startMinute must be before endMinute',
  });
export type AvailabilityRuleInputDto = z.infer<typeof availabilityRuleInputSchema>;

export const replaceAvailabilityRulesSchema = z.object({
  rules: z.array(availabilityRuleInputSchema),
});
export type ReplaceAvailabilityRulesDto = z.infer<typeof replaceAvailabilityRulesSchema>;

export const createAvailabilityOverrideSchema = z
  .object({
    date: dateOnlySchema,
    startMinute: z.number().int().min(0).max(1440),
    endMinute: z.number().int().min(0).max(1440),
    kind: availabilityOverrideKindSchema,
  })
  .refine((r) => r.startMinute < r.endMinute, {
    message: 'startMinute must be before endMinute',
  });
export type CreateAvailabilityOverrideDto = z.infer<typeof createAvailabilityOverrideSchema>;

export const bookingPreviewQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).optional(),
  /** Query strings arrive as text — coerce before literal union. */
  duration: z.coerce.number().pipe(bookingDurationSchema).optional(),
});
export type BookingPreviewQuery = z.infer<typeof bookingPreviewQuerySchema>;

export const publicBookingSlotsQuerySchema = z.object({
  duration: z.coerce.number().pipe(bookingDurationSchema),
});
export type PublicBookingSlotsQuery = z.infer<typeof publicBookingSlotsQuerySchema>;

export const createPublicBookingSchema = z.object({
  startAt: isoDateTimeSchema,
  duration: bookingDurationSchema,
  inviteeName: z.string().min(1).max(200),
  inviteeEmail: z.string().email().max(254),
  inviteeNote: z.string().max(5000).optional(),
  inviteeTimezone: z.string().min(1).max(64),
});
export type CreatePublicBookingDto = z.infer<typeof createPublicBookingSchema>;

export const bookingListQuerySchema = z.object({
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
});
export type BookingListQuery = z.infer<typeof bookingListQuerySchema>;

export const bookingCancelResponseSchema = z.object({
  ok: z.literal(true),
  alreadyCancelled: z.boolean(),
});
export type BookingCancelResponseDto = z.infer<typeof bookingCancelResponseSchema>;

export const sendBookingVideoLinkSchema = z.object({
  videoUrl: z.string().trim().min(1).max(2048).url('Введите корректную ссылку'),
});
export type SendBookingVideoLinkDto = z.infer<typeof sendBookingVideoLinkSchema>;
